import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLine, Overline, EASE } from "./Reveal";
import { scrollToId } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yRing = useTransform(scrollYProgress, [0, 1], [0, -110]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 55, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 55, damping: 18 });

  const onMouse = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="topo"
      ref={ref}
      onMouseMove={onMouse}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-28 pb-16"
      data-testid="hero-section"
    >
      <motion.div
        style={{ y: yBg }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden
      >
        <span className="text-stroke-royal select-none whitespace-nowrap font-display text-[22vw] font-bold leading-none tracking-tight">
          Creative
        </span>
      </motion.div>

      <motion.div
        style={{ y: yRing, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="pointer-events-none absolute right-[6%] top-[16%] hidden lg:block"
        aria-hidden
      >
        <div className="relative h-72 w-72" style={{ perspective: 900 }}>
          <div className="absolute inset-0 rounded-full border border-royal/25" />
          <div className="absolute inset-6 animate-spin-slow rounded-full border border-dashed border-royal/35" />
          <div className="absolute inset-16 rounded-full bg-royal/10" />
          <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-royal" />
          <div className="absolute -right-2 top-10 h-5 w-5 rounded-full border-2 border-royal bg-white" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mb-8"
        >
          <Overline>Estratégia · Marketing · Design · Fotografia · Vídeo</Overline>
        </motion.div>

        <h1 className="max-w-5xl font-display text-5xl font-medium leading-[1.02] tracking-tight text-royal-deep sm:text-6xl lg:text-[5.4rem]">
          <MaskLine delay={1.75}>Criamos o que faz</MaskLine>
          <MaskLine delay={1.88}>
            as marcas <em className="text-royal">avançar.</em>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 2.25, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
          data-testid="hero-subheadline"
        >
          Estratégia, marketing e criatividade reunidos numa só equipa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 2.4, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#sobre")}
            className="group flex items-center gap-3 rounded-full bg-royal px-8 py-4 text-sm font-bold text-white transition-all duration-500 hover:bg-black/10 hover:shadow-[0_12px_40px_rgba(42,80,170,0.4)]"
            data-testid="hero-cta-primary"
          >
            Conhecer a Creative
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
          </button>
          <button
            onClick={() => scrollToId("#projetos")}
            className="group flex items-center gap-3 rounded-full border border-royal/30 px-8 py-4 text-sm font-bold text-royal transition-all duration-500 hover:border-royal hover:bg-royal/10"
            data-testid="hero-cta-secondary"
          >
            Ver projetos
            <ArrowDown className="h-4 w-4 transition-transform duration-500 group-hover:translate-y-1" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2.9 }}
          className="mt-20 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground"
        >
          <span className="h-px w-14 bg-royal/40" />
          Agência criativa independente
        </motion.div>
      </div>
    </section>
  );
}
